import { create } from "zustand";
import {
  EquipmentCategoryType,
  EquipmentSubcategoryType,
  EmergencyCategoryType,
  EmergencySubcategoryType,
  HazardMaterialCategoryType,
  IncidentSeverityLevelsType,
  LocationType,
  maintenanceTypeListType,
  ManufacturerListType,
  UnitsofMeasureType,
  DisposalMethodsListType,
  ComplianceTypeListType,
  RoundType,
  EquipmentManagementType,
} from "../types/api";
import axiosInstance from "../common/axiosInstance";

export const useEquipmentCategoryStore = create<{
  equipmentCategory: EquipmentCategoryType[];
  loading: boolean;
  error: boolean;
  fetchAllEquipmentCategory: () => Promise<boolean>;
  setEquipmentCategory: (equipmentCategory: EquipmentCategoryType[]) => void;
  createEquipmentCategory: (
    equipmentCategory: EquipmentCategoryType,
  ) => Promise<boolean>;
  updateEquipmentCategory: (
    equipmentCategory: EquipmentCategoryType,
    id: string,
  ) => Promise<boolean>;
  deleteEquipmentCategory: (id: string) => Promise<boolean>;
  fetchEquipmentCategoryById: (
    id: string,
  ) => Promise<EquipmentCategoryType | null>;
}>((set, get) => ({
  equipmentCategory: [],
  loading: false,
  error: false,
  setEquipmentCategory: (equipmentCategory) => set({ equipmentCategory }),
  fetchAllEquipmentCategory: async () => {
    set({ loading: true, error: false });
    try {
      const response = await axiosInstance.get(
        `/api/equipmentcategory/all`,
      );
      set({
        equipmentCategory: response?.data?.data,
        loading: false,
        error: false,
      });
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    } finally {
      set({ loading: false });
    }
    return true;
  },
  fetchEquipmentCategoryById: async (id: string) => {
    set({ loading: true, error: false });
    try {
      const response = await axiosInstance.get(
        `/api/equipmentcategory/${id}`,
      );
      if (response?.data?.success) {
        return response?.data?.data;
      }
      return null;
    } catch (error) {
      set({ error: true, loading: false });
      return null;
    } finally {
      set({ loading: false });
    }
  },
  createEquipmentCategory: async (equipmentCategory: EquipmentCategoryType) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.post(
        `/api/equipmentcategory/create`,
        equipmentCategory,
      );
      await get().fetchAllEquipmentCategory();
      set({ loading: false, error: false });
      return true;
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    }
  },
  updateEquipmentCategory: async (
    equipmentCategory: EquipmentCategoryType,
    id: string,
  ) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.put(
        `/api/equipmentcategory/${id}`,
        equipmentCategory,
      );
      await get().fetchAllEquipmentCategory();
      set({ loading: false, error: false });
      return true;
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    }
  },
  deleteEquipmentCategory: async (id: string) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.delete(`/api/equipmentcategory/${id}`);
      set({
        equipmentCategory: get().equipmentCategory.filter(
          (category) => category._id !== id,
        ),
        loading: false,
        error: false,
      });
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    } finally {
      set({ loading: false });
    }
    return true;
  },
}));

interface EquipmentSubcategoryState {
  equipmentSubcategory: EquipmentSubcategoryType[];
  loading: boolean;
  error: boolean;
  fetchAllEquipmentSubcategory: () => Promise<boolean>;
  setEquipmentSubcategory: (
    equipmentSubcategory: EquipmentSubcategoryType[],
  ) => void;
  createEquipmentSubcategory: (
    equipmentSubcategory: EquipmentSubcategoryType,
  ) => Promise<boolean>;
  updateEquipmentSubcategory: (
    equipmentSubcategory: EquipmentSubcategoryType,
    id: string,
  ) => Promise<boolean>;
  deleteEquipmentSubcategory: (id: string) => Promise<boolean>;
  fetchEquipmentSubcategoryById: (
    id: string,
  ) => Promise<EquipmentSubcategoryType | null>;
}

export const useEquipmentSubcategoryStore = create<EquipmentSubcategoryState>(
  (set, get) => ({
    equipmentSubcategory: [],
    loading: false,
    error: false,
    setEquipmentSubcategory: (equipmentSubcategory) =>
      set({ equipmentSubcategory }),
    fetchAllEquipmentSubcategory: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/equipmentsubcategory/all`,
        );
        set({
          equipmentSubcategory: response?.data?.data,
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
    fetchEquipmentSubcategoryById: async (id: string) => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/equipmentsubcategory/${id}`,
        );
        if (response?.data?.success) {
          return response?.data?.data;
        }
        return null;
      } catch (error) {
        set({ error: true, loading: false });
        return null;
      } finally {
        set({ loading: false });
      }
    },
    createEquipmentSubcategory: async (
      equipmentSubcategory: EquipmentSubcategoryType,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(
          `/api/equipmentsubcategory/create`,
          equipmentSubcategory,
        );
        await get().fetchAllEquipmentSubcategory();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    updateEquipmentSubcategory: async (
      equipmentSubcategory: EquipmentSubcategoryType,
      id: string,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(
          `/api/equipmentsubcategory/${id}`,
          equipmentSubcategory,
        );
        await get().fetchAllEquipmentSubcategory();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    deleteEquipmentSubcategory: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/equipmentsubcategory/${id}`);
        set({
          equipmentSubcategory: get().equipmentSubcategory.filter(
            (subcategory) => subcategory._id !== id,
          ),
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
  }),
);

export const useEmergencyCategoryStore = create<{
  emergencyCategory: EmergencyCategoryType[];
  loading: boolean;
  error: boolean;
  fetchAllEmergencyCategory: () => Promise<boolean>;
  setEmergencyCategory: (emergencyCategory: EmergencyCategoryType[]) => void;
  createEmergencyCategory: (
    emergencyCategory: EmergencyCategoryType,
  ) => Promise<boolean>;
  updateEmergencyCategory: (
    emergencyCategory: EmergencyCategoryType,
    id: string,
  ) => Promise<boolean>;
  deleteEmergencyCategory: (id: string) => Promise<boolean>;
  fetchEmergencyCategoryById: (
    id: string,
  ) => Promise<EmergencyCategoryType | null>;
}>((set, get) => ({
  emergencyCategory: [],
  loading: false,
  error: false,
  setEmergencyCategory: (emergencyCategory) => set({ emergencyCategory }),
  fetchAllEmergencyCategory: async () => {
    set({ loading: true, error: false });
    try {
      const response = await axiosInstance.get(
        `/api/emergencycategory/all`,
      );
      set({
        emergencyCategory: response?.data?.data,
        loading: false,
        error: false,
      });
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    } finally {
      set({ loading: false });
    }
    return true;
  },
  fetchEmergencyCategoryById: async (id: string) => {
    set({ loading: true, error: false });
    try {
      const response = await axiosInstance.get(
        `/api/emergencycategory/${id}`,
      );
      if (response?.data?.success) {
        return response?.data?.data;
      }
      return null;
    } catch (error) {
      set({ error: true, loading: false });
      return null;
    } finally {
      set({ loading: false });
    }
  },
  createEmergencyCategory: async (emergencyCategory: EmergencyCategoryType) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.post(
        `/api/emergencycategory/create`,
        emergencyCategory,
      );
      await get().fetchAllEmergencyCategory();
      set({ loading: false, error: false });
      return true;
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    }
  },
  updateEmergencyCategory: async (
    emergencyCategory: EmergencyCategoryType,
    id: string,
  ) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.put(
        `/api/emergencycategory/${id}`,
        emergencyCategory,
      );
      await get().fetchAllEmergencyCategory();
      set({ loading: false, error: false });
      return true;
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    }
  },
  deleteEmergencyCategory: async (id: string) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.delete(`/api/emergencycategory/${id}`);
      set({
        emergencyCategory: get().emergencyCategory.filter(
          (category) => category._id !== id,
        ),
        loading: false,
        error: false,
      });
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    } finally {
      set({ loading: false });
    }
    return true;
  },
}));

interface EmergencySubcategoryState {
  emergencySubcategory: EmergencySubcategoryType[];
  loading: boolean;
  error: boolean;
  fetchAllEmergencySubcategory: () => Promise<boolean>;
  setEmergencySubcategory: (
    emergencySubcategory: EmergencySubcategoryType[],
  ) => void;
  createEmergencySubcategory: (
    emergencySubcategory: EmergencySubcategoryType,
  ) => Promise<boolean>;
  updateEmergencySubcategory: (
    emergencySubcategory: EmergencySubcategoryType,
    id: string,
  ) => Promise<boolean>;
  deleteEmergencySubcategory: (id: string) => Promise<boolean>;
  fetchEmergencySubcategoryById: (
    id: string,
  ) => Promise<EmergencySubcategoryType | null>;
}

export const useEmergencySubcategoryStore = create<EmergencySubcategoryState>(
  (set, get) => ({
    emergencySubcategory: [],
    loading: false,
    error: false,
    setEmergencySubcategory: (emergencySubcategory) =>
      set({ emergencySubcategory }),
    fetchAllEmergencySubcategory: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/emergencysubcategory/all`,
        );
        set({
          emergencySubcategory: response?.data?.data,
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
    fetchEmergencySubcategoryById: async (id: string) => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/emergencysubcategory/${id}`,
        );
        if (response?.data?.success) {
          return response?.data?.data;
        }
        return null;
      } catch (error) {
        set({ error: true, loading: false });
        return null;
      } finally {
        set({ loading: false });
      }
    },
    createEmergencySubcategory: async (
      emergencySubcategory: EmergencySubcategoryType,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(
          `/api/emergencysubcategory/create`,
          emergencySubcategory,
        );
        await get().fetchAllEmergencySubcategory();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    updateEmergencySubcategory: async (
      emergencySubcategory: EmergencySubcategoryType,
      id: string,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(
          `/api/emergencysubcategory/${id}`,
          emergencySubcategory,
        );
        await get().fetchAllEmergencySubcategory();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    deleteEmergencySubcategory: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/emergencysubcategory/${id}`);
        set({
          emergencySubcategory: get().emergencySubcategory.filter(
            (subcategory) => subcategory._id !== id,
          ),
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
  }),
);

interface ManufacturerListState {
  manufacturerList: ManufacturerListType[];
  loading: boolean;
  error: boolean;
  fetchAllManufacturerList: () => Promise<boolean>;
  createManufacturerList: (
    manufacturerList: ManufacturerListType,
  ) => Promise<boolean>;
  updateManufacturerList: (
    manufacturerList: ManufacturerListType,
    id: string,
  ) => Promise<boolean>;
  deleteManufacturerList: (id: string) => Promise<boolean>;
}

export const useManufacturerListStore = create<ManufacturerListState>(
  (set, get) => ({
    manufacturerList: [],
    loading: false,
    error: false,
    fetchAllManufacturerList: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/manufacturerlist/all`,
        );
        set({
          manufacturerList: response?.data?.data,
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
    createManufacturerList: async (manufacturerList: ManufacturerListType) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(
          `/api/manufacturerlist/create`,
          manufacturerList,
        );
        await get().fetchAllManufacturerList();
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    updateManufacturerList: async (
      manufacturerList: ManufacturerListType,
      id: string,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(
          `/api/manufacturerlist/${id}`,
          manufacturerList,
        );
        await get().fetchAllManufacturerList();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    deleteManufacturerList: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/manufacturerlist/${id}`);
        set({
          manufacturerList: get().manufacturerList.filter(
            (manufacturer) => manufacturer._id !== id,
          ),
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
  }),
);

interface maintenanceTypeListState {
  maintenanceTypeList: maintenanceTypeListType[];
  loading: boolean;
  error: boolean;
  fetchAllMaintenanceTypeList: () => Promise<boolean>;
  createMaintenanceTypeList: (
    maintenanceTypeList: maintenanceTypeListType,
  ) => Promise<boolean>;
  updateMaintenanceTypeList: (
    maintenanceTypeList: maintenanceTypeListType,
    id: string,
  ) => Promise<boolean>;
  deleteMaintenanceTypeList: (id: string) => Promise<boolean>;
}

export const useMaintenanceTypeListStore = create<maintenanceTypeListState>(
  (set, get) => ({
    maintenanceTypeList: [],
    loading: false,
    error: false,
    fetchAllMaintenanceTypeList: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/maintenancetypelist/all`,
        );
        set({
          maintenanceTypeList: response?.data?.data,
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
      return true;
    },
    createMaintenanceTypeList: async (
      maintenanceTypeList: maintenanceTypeListType,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(
          `/api/maintenancetypelist/create`,
          maintenanceTypeList,
        );
        await get().fetchAllMaintenanceTypeList();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    updateMaintenanceTypeList: async (
      maintenanceTypeList: maintenanceTypeListType,
      id: string,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(
          `/api/maintenancetypelist/${id}`,
          maintenanceTypeList,
        );
        await get().fetchAllMaintenanceTypeList();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    deleteMaintenanceTypeList: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/maintenancetypelist/${id}`);
        set({
          maintenanceTypeList: get().maintenanceTypeList.filter(
            (type) => type._id !== id,
          ),
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
  }),
);

interface UnitsofMeasureState {
  unitsofMeasure: UnitsofMeasureType[];
  unitsofMeasureMap: Map<string, string>;
  loading: boolean;
  error: boolean;
  fetchAllUnitsofMeasure: () => Promise<boolean>;
  createUnitsofMeasure: (
    unitsofMeasure: UnitsofMeasureType,
  ) => Promise<boolean>;
  updateUnitsofMeasure: (
    unitsofMeasure: UnitsofMeasureType,
    id: string,
  ) => Promise<boolean>;
  deleteUnitsofMeasure: (id: string) => Promise<boolean>;
}

export const useUnitsofMeasureStore = create<UnitsofMeasureState>(
  (set, get) => ({
    unitsofMeasure: [],
    unitsofMeasureMap: new Map(),
    loading: false,
    error: false,
    fetchAllUnitsofMeasure: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/unitsofmeasure/all`,
        );
        const unitsofMeasure = response?.data?.data;
        const unitsofMeasureMap = new Map<string, string>(
          unitsofMeasure.map((unit: UnitsofMeasureType) => [
            unit._id,
            unit.unitName,
          ]),
        );
        set({
          unitsofMeasure,
          unitsofMeasureMap,
          loading: false,
          error: false,
        });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    createUnitsofMeasure: async (unitsofMeasure: UnitsofMeasureType) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(
          `/api/unitsofmeasure/create`,
          unitsofMeasure,
        );
        await get().fetchAllUnitsofMeasure();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    updateUnitsofMeasure: async (
      unitsofMeasure: UnitsofMeasureType,
      id: string,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(
          `/api/unitsofmeasure/${id}`,
          unitsofMeasure,
        );
        await get().fetchAllUnitsofMeasure();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    deleteUnitsofMeasure: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/unitsofmeasure/${id}`);
        set({
          unitsofMeasure: get().unitsofMeasure.filter(
            (unit) => unit._id !== id,
          ),
          loading: false,
          error: false,
        });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
  }),
);

interface IncidentSeverityLevelsState {
  incidentSeverityLevels: IncidentSeverityLevelsType[];
  incidentSeverityLevelsMap: Map<string, string>;
  loading: boolean;
  error: boolean;
  fetchAllIncidentSeverityLevels: () => Promise<boolean>;
  createIncidentSeverityLevels: (
    incidentSeverityLevels: IncidentSeverityLevelsType,
  ) => Promise<boolean>;
  updateIncidentSeverityLevels: (
    incidentSeverityLevels: IncidentSeverityLevelsType,
    id: string,
  ) => Promise<boolean>;
  deleteIncidentSeverityLevels: (id: string) => Promise<boolean>;
}

export const useIncidentSeverityLevelsStore =
  create<IncidentSeverityLevelsState>((set, get) => ({
    incidentSeverityLevels: [],
    incidentSeverityLevelsMap: new Map(),
    loading: false,
    error: false,
    createIncidentSeverityLevels: async (
      incidentSeverityLevels: IncidentSeverityLevelsType,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(
          `/api/incidentseveritylevels/create`,
          incidentSeverityLevels,
        );
        await get().fetchAllIncidentSeverityLevels();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    updateIncidentSeverityLevels: async (
      incidentSeverityLevels: IncidentSeverityLevelsType,
      id: string,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(
          `/api/incidentseveritylevels/${id}`,
          incidentSeverityLevels,
        );
        await get().fetchAllIncidentSeverityLevels();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    deleteIncidentSeverityLevels: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/incidentseveritylevels/${id}`);
        set({
          incidentSeverityLevels: get().incidentSeverityLevels.filter(
            (level) => level._id !== id,
          ),
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
    fetchAllIncidentSeverityLevels: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/incidentseveritylevels/all`,
        );
        const incidentSeverityLevels = response?.data?.data;
        const incidentSeverityLevelsMap = new Map<string, string>(
          incidentSeverityLevels.map((level: IncidentSeverityLevelsType) => [
            level._id,
            level.incidentSeverityLevelName,
          ]),
        );
        set({
          incidentSeverityLevels,
          incidentSeverityLevelsMap,
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
  }));

interface HazardMaterialCategoryState {
  hazardMaterialCategory: HazardMaterialCategoryType[];
  hazardMaterialCategoryMap: Map<string, string>;
  loading: boolean;
  error: boolean;
  fetchAllHazardMaterialCategory: () => Promise<boolean>;
  createHazardMaterialCategory: (
    hazardMaterialCategory: HazardMaterialCategoryType,
  ) => Promise<boolean>;
  updateHazardMaterialCategory: (
    hazardMaterialCategory: HazardMaterialCategoryType,
    id: string,
  ) => Promise<boolean>;
  deleteHazardMaterialCategory: (id: string) => Promise<boolean>;
}

export const useHazardMaterialCategoryStore =
  create<HazardMaterialCategoryState>((set, get) => ({
    hazardMaterialCategory: [],
    hazardMaterialCategoryMap: new Map(),
    loading: false,
    error: false,
    fetchAllHazardMaterialCategory: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/hazardmaterialcategory/all`,
        );
        const hazardMaterialCategory = response?.data?.data;
        const hazardMaterialCategoryMap = new Map<string, string>(
          hazardMaterialCategory.map((category: HazardMaterialCategoryType) => [
            category._id,
            category.hazardMaterialCategoryName,
          ]),
        );
        set({
          hazardMaterialCategory,
          hazardMaterialCategoryMap,
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
    createHazardMaterialCategory: async (
      hazardMaterialCategory: HazardMaterialCategoryType,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(
          `/api/hazardmaterialcategory/create`,
          hazardMaterialCategory,
        );
        await get().fetchAllHazardMaterialCategory();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    updateHazardMaterialCategory: async (
      hazardMaterialCategory: HazardMaterialCategoryType,
      id: string,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(
          `/api/hazardmaterialcategory/${id}`,
          hazardMaterialCategory,
        );
        await get().fetchAllHazardMaterialCategory();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    deleteHazardMaterialCategory: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/hazardmaterialcategory/${id}`);
        set({
          hazardMaterialCategory: get().hazardMaterialCategory.filter(
            (category) => category._id !== id,
          ),
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
  }));

interface DisposalMethodsListState {
  disposalMethodsList: DisposalMethodsListType[];
  disposalMethodsListMap: Map<string, string>;
  loading: boolean;
  error: boolean;
  fetchAllDisposalMethodsList: () => Promise<boolean>;
  createDisposalMethodsList: (
    disposalMethodsList: DisposalMethodsListType,
  ) => Promise<boolean>;
  updateDisposalMethodsList: (
    disposalMethodsList: DisposalMethodsListType,
    id: string,
  ) => Promise<boolean>;
  deleteDisposalMethodsList: (id: string) => Promise<boolean>;
}

export const useDisposalMethodsListStore = create<DisposalMethodsListState>(
  (set, get) => ({
    disposalMethodsList: [],
    disposalMethodsListMap: new Map(),
    loading: false,
    error: false,
    fetchAllDisposalMethodsList: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/disposalmethodslist/all`,
        );
        const disposalMethodsList = response?.data?.data;
        const disposalMethodsListMap = new Map<string, string>(
          disposalMethodsList.map((method: DisposalMethodsListType) => [
            method._id,
            method.methodName,
          ]),
        );
        set({
          disposalMethodsList,
          disposalMethodsListMap,
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
    createDisposalMethodsList: async (
      disposalMethodsList: DisposalMethodsListType,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(
          `/api/disposalmethodslist/create`,
          disposalMethodsList,
        );
        await get().fetchAllDisposalMethodsList();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    updateDisposalMethodsList: async (
      disposalMethodsList: DisposalMethodsListType,
      id: string,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(
          `/api/disposalmethodslist/${id}`,
          disposalMethodsList,
        );
        await get().fetchAllDisposalMethodsList();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    deleteDisposalMethodsList: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/disposalmethodslist/${id}`);
        set({
          disposalMethodsList: get().disposalMethodsList.filter(
            (method) => method._id !== id,
          ),
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
      return true;
    },
  }),
);

interface LocationStore {
  allLocations: LocationType[];
  allLocationsMap: Map<string, string>;
  loading: boolean;
  error: boolean;

  fetchAllLocations: () => Promise<boolean>;
  createLocation: (data: LocationType) => Promise<boolean>;
  updateLocation: (data: LocationType, id: string) => Promise<boolean>;
  deleteLocation: (id: string) => Promise<boolean>;
}

export const useLocationStore = create<LocationStore>((set, get) => ({
  allLocations: [],
  allLocationsMap: new Map(),
  loading: false,
  error: false,

  fetchAllLocations: async () => {
    set({ loading: true, error: false });
    try {
      const response = await axiosInstance.get(`/api/location/all`);
      const allLocations = response.data.data || [];
      const allLocationsMap = new Map<string, string>(
        allLocations.map((location: LocationType) => [
          location._id,
          location.room,
        ]),
      );
      set({ allLocations, allLocationsMap, loading: false });
      return true;
    } catch {
      set({ loading: false, error: true });
      return false;
    }
  },

  createLocation: async (data) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.post(`/api/location/create`, data);
      await get().fetchAllLocations();
      set({ loading: false });
      return true;
    } catch {
      set({ loading: false, error: true });
      return false;
    }
  },

  updateLocation: async (data, id) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.put(`/api/location/${id}`, data);
      await get().fetchAllLocations();
      set({ loading: false });
      return true;
    } catch {
      set({ loading: false, error: true });
      return false;
    }
  },

  deleteLocation: async (id) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.delete(`/api/location/${id}`);
      await get().fetchAllLocations();
      set({ loading: false });
      return true;
    } catch {
      set({ loading: false, error: true });
      return false;
    }
  },
}));

interface ComplianceTypeListState {
  complianceTypeList: ComplianceTypeListType[];
  complianceTypeListMap: Map<string, string>;
  loading: boolean;
  error: boolean;
  fetchAllComplianceTypeList: () => Promise<boolean>;
  createComplianceTypeList: (
    complianceTypeList: ComplianceTypeListType,
  ) => Promise<boolean>;
  updateComplianceTypeList: (
    complianceTypeList: ComplianceTypeListType,
    id: string,
  ) => Promise<boolean>;
  deleteComplianceTypeList: (id: string) => Promise<boolean>;
}

export const useComplianceTypeListStore = create<ComplianceTypeListState>(
  (set, get) => ({
    complianceTypeList: [],
    complianceTypeListMap: new Map(),
    loading: false,
    error: false,
    fetchAllComplianceTypeList: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(`/api/compliance/get`);
        const complianceTypeList = response?.data?.data;
        const complianceTypeListMap = new Map<string, string>(
          complianceTypeList.map((type: ComplianceTypeListType) => [
            type._id!,
            type.complianceTypeName,
          ]),
        );
        set({
          complianceTypeList,
          complianceTypeListMap,
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      } finally {
        set({ loading: false });
      }
      return true;
    },
    createComplianceTypeList: async (
      complianceTypeList: ComplianceTypeListType,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(
          `/api/compliance/create`,
          complianceTypeList,
        );
        await get().fetchAllComplianceTypeList();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });

        return false;
      }
    },
    updateComplianceTypeList: async (
      complianceTypeList: ComplianceTypeListType,
      id: string,
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(
          `/api/compliance/update/${id}`,
          complianceTypeList,
        );
        await get().fetchAllComplianceTypeList();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
    deleteComplianceTypeList: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/compliance/${id}`);
        set({
          complianceTypeList: get().complianceTypeList.filter(
            (type) => type._id !== id,
          ),
          loading: false,
          error: false,
        });
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
      return true;
    },
  }),
);

interface RoundTypesState {
  roundTypes: RoundType[];
  loading: boolean;
  error: boolean;
  fetchAllRoundTypes: () => Promise<boolean>;
  createRoundType: (roundType: string) => Promise<boolean>;
  updateRoundType: (roundType: string, id: string) => Promise<boolean>;
  deleteRoundType: (id: string) => Promise<boolean>;
  getRoundTypeById: (id: string) => Promise<string | null>;
}

export const useRoundTypesStore = create<RoundTypesState>((set, get) => ({
  roundTypes: [],
  loading: false,
  error: false,
  fetchAllRoundTypes: async () => {
    set({ loading: true, error: false });
    try {
      const response = await axiosInstance.get(`/api/round-type/all`);
      set({ roundTypes: response.data.data, loading: false, error: false });
      return true;
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    }
  },
  createRoundType: async (roundType: string) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.post(
        `/api/round-type/create`,
        { roundType },
      );
      await get().fetchAllRoundTypes();
      set({ loading: false, error: false });
      return true;
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    }
  },
  updateRoundType: async (roundType: string, id: string) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.put(
        `/api/round-type/update/${id}`,
        { roundType },
      );
      await get().fetchAllRoundTypes();
      set({ loading: false, error: false });
      return true;
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    }
  },
  deleteRoundType: async (id: string) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.delete(`/api/round-type/${id}`);
      await get().fetchAllRoundTypes();
      set({ loading: false, error: false });
      return true;
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    }
  },
  getRoundTypeById: async (id: string) => {
    set({ loading: true, error: false });
    try {
      const response = await axiosInstance.get(`/api/round-type/${id}`);
      return response.data.data;
    } catch (error) {
      set({ error: true, loading: false });
      return null;
    }
  },
}));

export const useEquipmentManagementStore = create<{
  equipmentManagement: EquipmentManagementType[];
  loading: boolean;
  error: boolean;
  fetchAllEquipmentManagement: () => Promise<boolean>;
  setEquipmentManagement: (
    equipmentManagement: EquipmentManagementType[],
  ) => void;
  createEquipmentManagement: (
    equipmentManagement: EquipmentManagementType,
  ) => Promise<boolean>;
  updateEquipmentManagement: (
    equipmentManagement: EquipmentManagementType,
    id: string,
  ) => Promise<boolean>;
  deleteEquipmentManagement: (id: string) => Promise<boolean>;
  fetchEquipmentManagementById: (
    id: string,
  ) => Promise<EquipmentManagementType | null>;
}>((set, get) => ({
  equipmentManagement: [],
  loading: false,
  error: false,
  setEquipmentManagement: (equipmentManagement) => set({ equipmentManagement }),
  fetchAllEquipmentManagement: async () => {
    set({ loading: true, error: false });
    try {
      const response = await axiosInstance.get(
        `/api/equipment-management-master/all`,
      );
      set({
        equipmentManagement: response?.data?.results ?? [],
        loading: false,
        error: false,
      });
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    } finally {
      set({ loading: false });
    }
    return true;
  },
  fetchEquipmentManagementById: async (id: string) => {
    set({ loading: true, error: false });
    try {
      const response = await axiosInstance.get(
        `/api/equipment-management-master/${id}`,
      );
      if (response?.data?.success) {
        return response?.data?.message;
      }
      return null;
    } catch (error) {
      set({ error: true, loading: false });
      return null;
    } finally {
      set({ loading: false });
    }
  },
  createEquipmentManagement: async (
    equipmentManagement: EquipmentManagementType,
  ) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.post(
        `/api/equipment-management-master/create`,
        equipmentManagement,
      );
      await get().fetchAllEquipmentManagement();
      set({ loading: false, error: false });
      return true;
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    }
  },
  updateEquipmentManagement: async (
    equipmentManagement: EquipmentManagementType,
    id: string,
  ) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.put(
        `/api/equipment-management-master/${id}`,
        equipmentManagement,
      );
      await get().fetchAllEquipmentManagement();
      set({ loading: false, error: false });
      return true;
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    }
  },
  deleteEquipmentManagement: async (id: string) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.delete(
        `/api/equipment-management-master/${id}`,
      );
      set({
        equipmentManagement: get().equipmentManagement.filter(
          (equipmentManagement) => equipmentManagement._id !== id,
        ),
        loading: false,
        error: false,
      });
    } catch (error) {
      set({ error: true, loading: false });
      return false;
    } finally {
      set({ loading: false });
    }
    return true;
  },
}));
